import { computed, onBeforeUnmount, reactive, ref } from 'vue';
import apiClient from '@/utils/axios.js';
import echo from '@/echo.js';
import { useAuthStore } from '@/stores/auth-store.js';

/**
 * Everything Ask Inventree does, minus how it looks.
 *
 * Extracted because the feature has two faces — the full page at /ask and the
 * floating messenger that follows you around — and they must never drift. A
 * second copy of the polling, the socket teardown or the chip formatting is a
 * second place for a bug to live.
 *
 * Each caller gets its own instance, so the page and the launcher hold separate
 * conversations rather than fighting over one.
 */
export function useAsk() {
    const auth = useAuthStore();

    const question = ref('');
    const busy = ref(false);
    const errorMessage = ref('');
    const current = ref(null);
    const history = ref([]);
    const vocabulary = ref({ tables: [], metrics: [], reports: [] });

    const maxLength = 500;

    let echoChannel = null;
    let pollTimer = null;
    let watchingId = null;

    const pending = computed(() => current.value && !['completed', 'failed'].includes(current.value.status));
    const result = computed(() => current.value?.result ?? {});

    /**
     * Suggestions built from this user's own vocabulary rather than a fixed
     * list, so they are never an invitation to ask something that will be
     * refused.
     */
    const suggestions = computed(() => {
        const out = [];
        const metrics = vocabulary.value.metrics.map((m) => m.metric);
        const tables = vocabulary.value.tables.map((t) => t.table);

        if (metrics.includes('revenue')) out.push('How much did we sell last month?');
        if (metrics.includes('product_revenue')) out.push('What sold best last week?');
        if (tables.includes('sales')) out.push('Show unpaid sales from this month');
        if (tables.includes('purchases')) out.push('Which purchase orders are still unpaid?');
        if (tables.includes('damaged_items')) out.push('What damage was reported this month?');
        if (tables.includes('customers')) out.push('Customers in Lahore with a credit limit');

        return out.slice(0, 4);
    });

    const chips = computed(() => {
        const plan = current.value?.plan;
        if (!plan) return [];

        if (plan.intent === 'list' && plan.list) {
            const out = [humanise(plan.list.table)];

            if (plan.list.search) out.push(`matching "${plan.list.search}"`);

            for (const f of plan.list.filters ?? []) {
                out.push(`${humanise(f.key)} ${describeValue(f.value)}`);
            }

            if (plan.list.sortField) out.push(`by ${humanise(plan.list.sortField)} ${plan.list.sortDirection}`);

            return out;
        }

        if (plan.intent === 'measure' && plan.measure) {
            const out = [humanise(plan.measure.metric), `${plan.measure.window.from} to ${plan.measure.window.to}`];
            for (const d of plan.measure.dimensions ?? []) out.push(`by ${humanise(d)}`);
            return out;
        }

        return [];
    });

    function describeValue(value) {
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            if (value.min !== undefined && value.max !== undefined) return `between ${value.min} and ${value.max}`;
            if (value.min !== undefined) return `at least ${value.min}`;
            if (value.max !== undefined) return `up to ${value.max}`;
        }
        if (Array.isArray(value)) return `is ${value.join(' or ')}`;
        return `is ${value}`;
    }

    function humanise(key) {
        return String(key ?? '')
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    function rowLabel(row) {
        return row.branch_name ?? row.variant_name ?? row.period ?? row.damage_type ?? '—';
    }

    function cell(row, col) {
        const v = row?.[col];
        if (v === null || v === undefined || v === '') return '—';
        return v;
    }

    function formatValue(value, unit) {
        const n = Number(value ?? 0);
        if (unit === 'count') return n.toLocaleString();
        return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    function shortDate(iso) {
        if (!iso) return '';
        return new Date(iso).toLocaleDateString();
    }

    // ---------------------------------------------------------------
    // Asking
    // ---------------------------------------------------------------

    function askThis(text) {
        question.value = text;
        return ask();
    }

    async function ask() {
        const text = question.value.trim();
        if (text.length < 3 || busy.value) return;

        busy.value = true;
        errorMessage.value = '';
        current.value = null;
        stopWatching();

        try {
            const { data } = await apiClient.post('ask', { question: text }, { offlinePolicy: 'mutation' });

            current.value = data.question;
            question.value = '';
            startWatching(data.question.id);
            loadHistory();
        } catch (error) {
            errorMessage.value =
                error.response?.data?.message ||
                (error.code === 'ERR_OFFLINE_WRITE' ? 'You need to be online to ask a question.' : 'That question could not be sent.');
        } finally {
            busy.value = false;
        }
    }

    /**
     * Belt and braces, exactly as imports, exports and extractions do it: the
     * socket is the fast path and the poll covers a dropped connection.
     */
    function startWatching(id) {
        watchingId = id;

        const userId = auth.user?.id;

        if (userId && window.Echo) {
            echoChannel = echo.private(`ask.${userId}`);
            echoChannel.listen('.AskProgress', (event) => {
                if (event?.progress?.id === watchingId) handleProgress(event.progress);
            });
        }

        pollTimer = setInterval(async () => {
            try {
                const { data } = await apiClient.get(`ask/${watchingId}`, { skipProgress: true });
                handleProgress(data.question);
            } catch {
                // Keep polling until it finishes or the view is left.
            }
        }, 2000);
    }

    function handleProgress(progress) {
        if (!progress || progress.id !== watchingId) return;

        current.value = progress;

        if (['completed', 'failed'].includes(progress.status)) {
            stopWatching();
            loadHistory();
        }
    }

    function stopWatching() {
        if (pollTimer) {
            clearInterval(pollTimer);
            pollTimer = null;
        }

        if (echoChannel) {
            echoChannel.stopListening('.AskProgress');
            echoChannel = null;
        }

        const userId = auth.user?.id;
        if (userId && window.Echo) echo.leave(`ask.${userId}`);

        watchingId = null;
    }

    /**
     * Show a past answer. Stops any watch first, so a slow one cannot overwrite it.
     *
     * A history row is not always finished: reload the page while a question is
     * being worked out and it comes back from the list still processing. Picking
     * one up starts watching it again, because otherwise the pane sits on a
     * spinner that nothing is ever going to replace — `ask:cleanup` will fail
     * the row five minutes later and this session would never hear about it.
     */
    function show(item) {
        stopWatching();
        current.value = item;
        errorMessage.value = '';

        if (item?.id && !['completed', 'failed'].includes(item.status)) startWatching(item.id);
    }

    /**
     * Put the answer away without deleting it.
     *
     * It stays in history — this only clears the pane, which is what somebody
     * wants when they have read it and are looking at the screen underneath.
     */
    function dismiss() {
        stopWatching();
        current.value = null;
        errorMessage.value = '';
    }

    /**
     * Delete a question for good.
     *
     * Optimistic: the row leaves the list before the request returns, because
     * the alternative is a visible lag on a button whose whole purpose is
     * tidying up. A failure puts it back and says so.
     */
    async function remove(item) {
        const id = item?.id;
        if (!id) return;

        const index = history.value.findIndex((h) => h.id === id);
        const snapshot = index >= 0 ? history.value[index] : null;

        if (index >= 0) history.value.splice(index, 1);
        if (current.value?.id === id) dismiss();

        try {
            await apiClient.delete(`ask/${id}`, { skipProgress: true });
        } catch (error) {
            if (snapshot) history.value.splice(index, 0, snapshot);
            errorMessage.value = error.response?.data?.message || 'That question could not be deleted.';
        }
    }

    async function loadHistory() {
        try {
            const { data } = await apiClient.post('ask/list', { perPage: 15 }, { skipProgress: true });
            history.value = data.data.data ?? [];
        } catch {
            // History is a convenience; never block the panel on it.
        }
    }

    async function loadVocabulary() {
        try {
            const { data } = await apiClient.get('ask/vocabulary', { skipProgress: true });
            vocabulary.value = data.data;
        } catch {
            // Suggestions are optional; the input still works without them.
        }
    }

    // ---------------------------------------------------------------
    // Dictation
    // ---------------------------------------------------------------

    /**
     * Speech is done in the browser, not on the server.
     *
     * The Web Speech API is free, needs no round trip and no key, and returns
     * interim results as you talk. Sending audio to the model instead would add
     * a per-question bill and a wait to a feature whose whole appeal is
     * immediacy — and this is dictation into a text box, not transcription of a
     * recording, so accuracy that degrades on background noise is recoverable
     * by simply editing what landed.
     *
     * It is Chromium and Safari only. Firefox has no implementation, so the
     * button is hidden rather than shown broken.
     */
    const SpeechRecognition = typeof window !== 'undefined' ? (window.SpeechRecognition ?? window.webkitSpeechRecognition) : null;

    const speechSupported = Boolean(SpeechRecognition);
    const listening = ref(false);
    let recognition = null;

    /** What was in the box before dictation started, so interim results replace only the dictated part. */
    let dictationBase = '';

    function startDictation(locale = 'en-US') {
        if (!speechSupported || listening.value) return;

        recognition = new SpeechRecognition();
        recognition.lang = locale;
        recognition.continuous = false;
        // Interim results make it feel live; the final result overwrites them.
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        dictationBase = question.value.trim();

        recognition.onresult = (event) => {
            let text = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                text += event.results[i][0].transcript;
            }

            question.value = (dictationBase ? dictationBase + ' ' : '') + text.trim();
        };

        recognition.onerror = (event) => {
            listening.value = false;
            // 'aborted' and 'no-speech' are ordinary — somebody stopped, or said
            // nothing. Only a real fault is worth a message.
            if (!['aborted', 'no-speech'].includes(event.error)) {
                errorMessage.value =
                    event.error === 'not-allowed'
                        ? 'Microphone access was blocked. Allow it in your browser to dictate.'
                        : 'Dictation stopped unexpectedly.';
            }
        };

        recognition.onend = () => {
            listening.value = false;
            recognition = null;
        };

        try {
            recognition.start();
            listening.value = true;
            errorMessage.value = '';
        } catch {
            listening.value = false;
            recognition = null;
        }
    }

    function stopDictation() {
        if (recognition) {
            try {
                recognition.stop();
            } catch {
                // Already stopping.
            }
        }
        listening.value = false;
    }

    function toggleDictation(locale) {
        listening.value ? stopDictation() : startDictation(locale);
    }

    onBeforeUnmount(() => {
        stopWatching();
        stopDictation();
    });

    /*
     * Returned reactive, not as a bare object of refs.
     *
     * Every consumer holds this instance behind a name — `ask.current`,
     * `ask.pending` — either as a local binding or as a prop. Vue unwraps refs
     * in a template only at the top level of the setup state, never one property
     * deep, so a plain object here hands the template the Ref objects
     * themselves. `v-if="ask.current"` is then true against a null answer and
     * `v-if="ask.pending"` is true forever: the panel opens on a blank card
     * that says it is working through your data and never stops, and the error
     * line renders permanently with nothing in it. reactive() unwraps on read,
     * so the templates mean what they say.
     */
    return reactive({
        // state
        question,
        busy,
        errorMessage,
        current,
        history,
        vocabulary,
        maxLength,
        pending,
        result,
        suggestions,
        chips,
        // actions
        ask,
        askThis,
        show,
        dismiss,
        remove,
        loadHistory,
        loadVocabulary,
        stopWatching,
        // dictation
        speechSupported,
        listening,
        toggleDictation,
        stopDictation,
        // formatting
        humanise,
        rowLabel,
        cell,
        formatValue,
        shortDate,
    });
}
